/* 台湾 LINE Shopping：采集归因和行为事件；LINE 密钥仅保留在后端。 */
(() => {
  'use strict';
  // 读取 Liquid 输出的页面和商品信息；缺失或解析失败时不启用。
  const contextNode = document.getElementById('line-shopping-context');
  if (!contextNode) return;
  let context;
  try {
    context = JSON.parse(contextNode.textContent);
  } catch {
    return;
  }
  const attribute = 'line_shopping_ecid';
  const cookieName = 'rr_line_ecid';
  const nativeFetch = window.fetch.bind(window);
  const root = (context.root || '/').replace(/\/?$/, '/');
  // LINE 事件编号：注册、非商品页浏览、购物车、商品浏览。
  const EVENT_REGISTER = 1;
  const EVENT_PAGE_VIEW = 2;
  const EVENT_CART = 4;
  const EVENT_PRODUCT_VIEW = 5;

  const timeoutMs = 1500;
  const allowed = /^[a-zA-Z0-9]{64}$/;
  const resumed = new WeakSet();

  // 记录异常；日志失败也不能影响购物。
  function report(code) {
    try {
      console.warn('[LINE Shopping]', code);
    } catch {
      /* 忽略日志自身的异常。 */
    }
  }

  // 获取有效归因标识；Cookie 缺失、格式错误时返回空值。
  function ecid() {
    try {
      const part = document.cookie.split('; ').find((value) => {
        return value.indexOf(`${cookieName}=`) === 0;
      });
      const value = part ? decodeURIComponent(part.slice(cookieName.length + 1)) : '';
      return allowed.test(value) ? value : '';
    } catch {
      report('invalid-cookie');
      return '';
    }
  }

  // 新导购链接覆盖旧归因，Cookie 有效期为 24 小时。
  try {
    const incoming = new URL(location.href).searchParams.get('ecid');
    if (incoming && allowed.test(incoming)) {
      document.cookie = `${cookieName}=${encodeURIComponent(incoming)}; Max-Age=86400; Path=/; SameSite=Lax; Secure`;
      // 仅移除地址栏 ECID，保留其他参数，避免刷新页面延长归因有效期。
      const landing = new URL(location.href);
      landing.searchParams.delete('ecid');
      history.replaceState(history.state, '', landing.href);
    }
  } catch {
    report('capture-failed');
  }

  // 请求 JSON：统一检查 HTTP 错误，并限制请求及正文读取的总等待时间。
  async function request(url, options) {
    const controller = new AbortController();
    let timer;

    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {
        reject(new Error('timeout'));
        controller.abort();
      }, timeoutMs);
    });

    const work = async () => {
      const response = await nativeFetch(url, {
        ...options,
        signal: controller.signal,
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error('http');
      return response.json();
    };

    try {
      // 即使其他 fetch 包装忽略取消信号，也能按时结束等待。
      return await Promise.race([work(), timeout]);
    } finally {
      clearTimeout(timer);
    }
  }

  // 有有效 ECID 才向后端发送事件，由后端回传 LINE；失败仅记录，不重试。
  function event(id, products, url) {
    const value = ecid();
    if (!value) {
      return;
    }

    const target = new URL(url || location.href);
    target.searchParams.delete('ecid');

    const payload = {
      ecid: value,
      event_id: id,
      event_time: Math.floor(Date.now() / 1000),
      redirect_url: target.href,
    };

    if (products) {
      payload.products = products;
    }

    // 事件不依赖响应数据；独立发送，不沿用购物车请求的超时取消。
    try {
      nativeFetch('/a/ores/lineShopping/event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'same-origin',
        keepalive: true,
      })
        .then((response) => {
          if (!response.ok) report('event-failed');
        })
        .catch(() => {
          report('event-failed');
        });
    } catch {
      report('event-failed');
    }
  }

  // 按 Variant ID 组装商品；折后行金额从 Shopify 子单位转换为整数元。
  function cartProducts(cart) {
    return (cart.items || []).map((item) => ({
      product_id: String(item.variant_id || item.id),
      product_amount: Math.round(item.final_line_price / 100),
    }));
  }

  // 加购成功或进入购物车后，读取最新购物车并上报。
  function cartEvent() {
    if (!ecid()) {
      return;
    }

    request(`${root}cart.js`)
      .then((cart) => {
        const products = cartProducts(cart);

        if (products.length) {
          event(EVENT_CART, products, `${location.origin}${root}cart`);
        }
      })
      .catch(() => {
        report('cart-event-failed');
      });
  }

  // 结账前同步归因；失败或超时由调用方继续原购买流程。
  async function syncCart() {
    // 仅更新 LINE 属性；无有效 ECID 时写空值以清除旧归因，无需预读购物车。
    const attributes = { [attribute]: ecid() };

    try {
      const cart = await request(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      });

      const products = cartProducts(cart);

      if (products.length) {
        event(EVENT_CART, products, `${location.origin}${root}cart`);
      }
    } catch {
      report('cart-sync-failed');
    }
  }

  // 为同站直接购买链接附加 ECID，只返回 URL，不执行跳转。
  function permalink(url) {
    try {
      const target = new URL(url, location.href);

      if (target.origin === location.origin && /\/cart\/[0-9]+:[0-9]+/.test(target.pathname)) {
        // 此链接创建新的购物车，需要通过链接参数传递归因属性。
        target.searchParams.set(`attributes[${attribute}]`, ecid());
        return target.href;
      }
    } catch {
      report('permalink-failed');
    }

    return url;
  }

  // 直接购买入口提供规格和数量，由后端补充商品金额。
  function checkoutItems(items) {
    try {
      const products = items.map((item) => ({
        product_id: String(item.id),
        quantity: Number(item.quantity),
      }));

      if (products.length) {
        event(EVENT_CART, products, `${location.origin}${root}cart`);
      }
    } catch {
      report('checkout-event-failed');
    }
  }

  // 解析 /cart/规格ID:数量 链接，上报直接购买涉及的商品。
  function permalinkEvent(url) {
    try {
      const target = new URL(url, location.href);
      const match = target.pathname.match(/\/cart\/([0-9:,]+)$/);

      if (target.origin === location.origin && match) {
        checkoutItems(
          match[1].split(',').map((item) => {
            const [id, quantity] = item.split(':');

            return { id, quantity };
          }),
        );
      }
    } catch {
      report('permalink-event-failed');
    }
  }

  // 供脚本触发的直接购买使用：发起上报后携带 ECID 跳转，不等待上报。
  function go(url) {
    permalinkEvent(url);
    location.assign(permalink(url));
  }

  // 供主题现有加购、积分购买等入口显式调用，不覆盖全局网络方法。
  window.LineShopping = {
    ecid,
    syncCart,
    permalink,
    cartEvent,
    checkoutItems,
    go,
  };

  // 在链接默认跳转前补充直接购买参数，保留原生点击行为。
  window.addEventListener(
    'click',
    (e) => {
      const anchor = e.target.closest?.('a[href]');
      if (!anchor) {
        return;
      }

      const href = anchor.getAttribute('href');
      const annotated = permalink(href);

      if (annotated !== href) {
        anchor.href = annotated;
      }

      permalinkEvent(anchor.href);
    },
    true,
  );

  // 拦截购物车结账提交，同步属性后恢复原提交按钮和流程。
  window.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      // 恢复提交时放行一次，避免重复拦截。
      if (resumed.has(form)) {
        resumed.delete(form);
        return;
      }

      let path;
      try {
        path = new URL(form.action, location.href).pathname;
      } catch {
        return;
      }

      const submitter = e.submitter;
      if (!/\/cart\/?$/.test(path) || (submitter && submitter.name !== 'checkout')) {
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();

      syncCart().finally(() => {
        // 更新表单中的旧属性，避免提交时再次写回过期 ECID。
        const input = form.querySelector(`[name="attributes[${attribute}]"]`);
        if (input) {
          input.value = ecid();
        }

        resumed.add(form);

        if (form.requestSubmit) {
          form.requestSubmit(submitter || undefined);
        } else {
          const checkout = document.createElement('input');
          checkout.type = 'hidden';
          checkout.name = 'checkout';
          checkout.value = 'Checkout';
          form.appendChild(checkout);

          HTMLFormElement.prototype.submit.call(form);
        }
      });
    },
    true,
  );

  if (context.page === 'cart' || /\/pages\/app-cart$/.test(location.pathname)) {
    cartEvent();
  }

  // 登录状态仅触发核验请求，是否为新注册用户由后端判断。
  if (context.customer) {
    event(EVENT_REGISTER, null, `${location.origin}/`);
  }

  let viewTimer;
  let viewedVariant;

  // 优先读取页面所选规格，其次使用 URL 参数和服务端默认规格。
  function currentVariant() {
    if (!context.product) {
      return null;
    }

    const select = document.querySelector('.as-product-variant');
    const id =
      select?.value ||
      new URL(location.href).searchParams.get('variant') ||
      context.variantId;

    return context.product.variants.find((variant) => String(variant.id) === String(id));
  }

  // 可见页面停留超过 3 秒后上报；重新计时，避免连续重复上报同一规格。
  function scheduleView() {
    clearTimeout(viewTimer);

    if (document.visibilityState === 'hidden') {
      return;
    }

    const variant = currentVariant();
    const id = variant ? String(variant.id) : '';

    viewTimer = setTimeout(() => {
      if (variant) {
        if (viewedVariant === id) {
          return;
        }

        viewedVariant = id;

        event(
          EVENT_PRODUCT_VIEW,
          [
            {
              product_id: id,
              product_amount: Math.round(variant.price / 100),
            },
          ],
          `${location.origin}${location.pathname}?variant=${id}`,
        );
      } else {
        event(EVENT_PAGE_VIEW);
      }
    }, 3100);
  }

  document.addEventListener('visibilitychange', scheduleView);

  // 等待主题完成规格切换，再同步 URL、商品结构化数据并重新计时。
  document.addEventListener('change', () => {
    setTimeout(() => {
      const variant = currentVariant();

      if (variant) {
        const url = new URL(location.href);
        url.searchParams.set('variant', String(variant.id));
        history.replaceState(history.state, '', url.href);

        const schema = document.getElementById('line-shopping-product');

        if (schema) {
          try {
            const data = JSON.parse(schema.textContent);

            data.productID = String(variant.id);
            data.name =
              context.product.title +
              (variant.title === 'Default Title' ? '' : ` - ${variant.title}`);
            data.url = url.href;

            data.offers.url = url.href;
            data.offers.price = Math.round(variant.price / 100);
            data.offers.availability =
              context.availability?.[String(variant.id)] || 'https://schema.org/OutOfStock';

            if (variant.featured_image) {
              data.image = variant.featured_image.src;
            }

            schema.textContent = JSON.stringify(data);
          } catch {
            report('variant-schema-failed');
          }
        }
      }

      scheduleView();
    }, 0);
  });

  window.addEventListener('popstate', scheduleView);

  scheduleView();
})();
